import React, { useEffect, useRef, useState } from 'react';
import { api, buildAssetUrl, mensajeError } from '../lib/api';
import { PageHeader, SectionCard } from '../components/Common';
import ConectorMcpPanel from '../components/ConectorMcpPanel';
import AjustesMarcas from '../components/AjustesMarcas';
import AjustesCobros from '../components/AjustesCobros';
import AjustesPaciente, { AvisosConfiguracion } from '../components/AjustesPaciente';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Save, Upload, Loader2, Link2 } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { isManagement } from '../lib/roles';

function ImageUpload({ label, currentFileId, kind, onUploaded, soloLectura = false }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post(`/api/settings/upload/${kind}`, fd);
      toast.success(`${label} actualizado`);
      onUploaded && onUploaded(res.data.file_id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-lg border border-dashed border-border bg-[rgba(46,110,94,0.03)] flex items-center justify-center overflow-hidden">
          {currentFileId ? <img src={buildAssetUrl(currentFileId)} alt={label} className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-muted-foreground text-center">Sin imagen</span>}
        </div>
        {!soloLectura && (
          <>
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid={`settings-upload-${kind}-input`} />
            <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`settings-upload-${kind}-button`}>
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo…</> : <><Upload className="w-4 h-4 mr-2" /> Subir</>}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  // Quién cambia la configuración (M31, informe del 22/9/2026): el servidor
  // solo deja guardar y subir a dirección (`require_management`: admin,
  // dirección y sus roles viejos); a administrativo, enfermería y anestesista
  // «Guardar cambios» y «Subir» les daban un 403. Ellas siguen entrando —aquí
  // están las comprobaciones diarias, que son de todo el equipo— y ven lo que
  // hay, pero sin botones que el servidor les va a rechazar.
  const { user } = useAuth();
  const soloLectura = !isManagement(user);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  // La puerta de al lado de M31: si la carga fallaba, la pantalla salía con
  // los campos en blanco, como si la clínica no tuviera nombre ni teléfono
  // (y el admin podía guardar encima). Un error se dice; no se pinta como dato.
  const [errorCarga, setErrorCarga] = useState('');
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/settings');
      setSettings(r.data); setForm(r.data || {}); setErrorCarga('');
    } catch (err) {
      setErrorCarga(mensajeError(err, 'No se ha podido cargar la configuración.'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/api/settings', form);
      setSettings(r.data); toast.success('Configuración guardada');
    } catch (err) { toast.error(mensajeError(err, 'No se ha podido guardar la configuración.')); } finally { setBusy(false); }
  };
  if (loading) return <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>;
  if (errorCarga) {
    return (
      <div>
        <PageHeader title="Configuración" subtitle="Identidad de la clínica, logos, firma y sello del doctor." />
        <div className="cp-card p-6 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg" data-testid="settings-error-carga">
          <p>{errorCarga}</p>
          <Button variant="outline" className="mt-3" onClick={load} data-testid="settings-reintentar">Reintentar</Button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <PageHeader title="Configuración"
        subtitle={soloLectura
          ? 'Identidad de la clínica, logos, firma y sello del doctor. Solo dirección puede cambiarlos.'
          : 'Identidad de la clínica, logos, firma y sello del doctor.'}
        actions={soloLectura ? null : <Button onClick={save} disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="settings-save-button"><Save className="w-4 h-4 mr-2" /> {busy ? 'Guardando…' : 'Guardar cambios'}</Button>} />
      {/* Lo que falta para que el paciente lo vea todo (sin valores de
          respaldo en el código); se vuelve a mirar al guardar. */}
      <AvisosConfiguracion version={settings} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Datos de la clínica">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Nombre clínica</Label><Input disabled={soloLectura} value={form.clinic_name || ''} onChange={(e) => setF('clinic_name', e.target.value)} /></div>
            <div><Label>Razón social</Label><Input disabled={soloLectura} value={form.legal_entity || ''} onChange={(e) => setF('legal_entity', e.target.value)} /></div>
            <div><Label>Teléfono</Label><Input disabled={soloLectura} value={form.phone || ''} onChange={(e) => setF('phone', e.target.value)} /></div>
            <div className="md:col-span-2"><Label>Dirección</Label><Input disabled={soloLectura} value={form.address || ''} onChange={(e) => setF('address', e.target.value)} /></div>
            <div><Label>Email</Label><Input disabled={soloLectura} value={form.email || ''} onChange={(e) => setF('email', e.target.value)} /></div>
            <div><Label>Web</Label><Input disabled={soloLectura} value={form.website || ''} onChange={(e) => setF('website', e.target.value)} /></div>
            <div><Label>Doctor</Label><Input disabled={soloLectura} value={form.doctor_name || ''} onChange={(e) => setF('doctor_name', e.target.value)} /></div>
            <div><Label>Nº colegiado</Label><Input disabled={soloLectura} value={form.doctor_license || ''} onChange={(e) => setF('doctor_license', e.target.value)} /></div>
          </div>
        </SectionCard>
        <SectionCard title="Logos">
          <div className="space-y-5">
            <ImageUpload label="Logo claro (sobre fondo blanco)" kind="logo_light" currentFileId={settings?.logo_light_path} onUploaded={() => load()} soloLectura={soloLectura} />
            <ImageUpload label="Logo para fondo oscuro" kind="logo_dark" currentFileId={settings?.logo_dark_path} onUploaded={() => load()} soloLectura={soloLectura} />
          </div>
        </SectionCard>
        {/* Comprobaciones diarias (2026-09-06 los enlaces del paciente,
            2026-09-10 el día de consulta de recepción): el estado vive en su
            propia pantalla, en dos pestañas; desde aquí se llega. */}
        <SectionCard title="Comprobaciones diarias">
          <p className="text-sm text-muted-foreground mb-3">
            Cada día laborable se comprueba, como paciente, que todos los enlaces que
            mandamos (confirmar cita, documento único, cuestionario de salud, guía de
            cirugía, paquete online y tablet) funcionan y se pueden rellenar y enviar.
            Y, con los ojos de recepción, que el día de consulta entero se puede hacer:
            agenda, citas, mensajes, cobros, papeles, ficha y quirófano.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" data-testid="settings-salud-enlaces-link">
              <Link to="/salud-enlaces"><Link2 className="w-4 h-4 mr-2" /> Ver el estado de los enlaces</Link>
            </Button>
            <Button asChild variant="outline" data-testid="settings-salud-recepcion-link">
              <Link to="/salud-enlaces?vista=recepcion"><Link2 className="w-4 h-4 mr-2" /> Ver el día de consulta</Link>
            </Button>
          </div>
        </SectionCard>
        <SectionCard title="Firma y sello del Doctor">
          <div className="space-y-5">
            <ImageUpload label="Firma del Doctor" kind="doctor_signature" currentFileId={settings?.doctor_signature_path} onUploaded={() => load()} soloLectura={soloLectura} />
            <ImageUpload label="Sello" kind="doctor_stamp" currentFileId={settings?.doctor_stamp_path} onUploaded={() => load()} soloLectura={soloLectura} />
            <p className="text-xs text-muted-foreground">Estos se incrustarán automáticamente en los consentimientos generados.</p>
          </div>
        </SectionCard>
        {/* Cuenta, pago de la reserva, reseñas y textos de los WhatsApp:
            los datos que usan los mensajes y la guía del paciente. */}
        <AjustesPaciente form={form} setF={setF} soloLectura={soloLectura} />
        {/* Los conceptos (y sus importes) que ofrece Cobros: de cada clínica. */}
        <AjustesCobros form={form} setF={setF} soloLectura={soloLectura} />
        <ConectorMcpPanel />
        {/* Las marcas de los leads: las da de alta cada clínica. */}
        <AjustesMarcas />
      </div>
    </div>
  );
}
